import { useEffect, useRef, useState } from 'preact/hooks'
import useProductForm from 'hooks/useProductForm'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import useBundleGlobal from 'hooks/useBundleGlobal'
import clsx from 'clsx'
import { select, on, off } from 'helpers/dom'
import throttle from 'lodash.throttle'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import ProductATC from 'snippets/product/product-atc/product-atc'
import ButtonBIS from 'snippets/button-atc/button-bis'
import ProductBundleFormATC from 'snippets/product/product-bundle/product-bundle-form-atc'

const selectors = {
  PRODUCT_INFO_SELECTOR: '.js-product-info'
}

function ProductStickyATC({
  isBundle = false,
  bundles,
  minItemsRequired,
  isBundleFixed,
  isDisabledATC,
  currentPrice,
  currentSellingPlan
}) {
  const [sellingPlanId, setSellingPlanId] = useState(null)
  const [isActive, setIsActive] = useState(false)
  const { currentVariant, initProduct, updateCurrentVariant } = useProductForm()
  const { isMobile } = useDetectBreakpoint()
  const { productBundles } = useBundleGlobal()
  const [currentVariantPrice, setCurrentVariantPrice] = useState(currentVariant.price)
  const stickyAtcRef = useRef(null)

  const { title, type, tags } = initProduct
  const productInfo = select(selectors.PRODUCT_INFO_SELECTOR)
  const hasNoNotifyMeTag = (tags.length > 0 && tags.some((tag) => tag === 'no-notify-me')) || false
  const enableBackInStock = !currentVariant.available && !hasNoNotifyMeTag

  const handleScroll = () => {
    if (!productInfo || !stickyAtcRef.current) return

    const { bottom } = productInfo.getBoundingClientRect()

    setIsActive(bottom - stickyAtcRef.current.offsetHeight <= 0)
  }

  const handleEditClick = () => {
    if (productInfo) {
      const offset = 100
      const topPosition = productInfo.getBoundingClientRect().top + document.documentElement.scrollTop - offset

      window.scrollTo({
        top: topPosition,
        behavior: 'smooth'
      })

      if (stickyAtcRef.current) {
        setIsActive(false)
      }
    }
  }

  useEffect(() => {
    handleScroll()
    on('scroll', throttle(handleScroll, 100), window)

    return () => off('scroll', handleScroll, window)
  }, [])

  useEffect(() => {
    const handlePriceUpdate = (newPrice) => {
      setCurrentVariantPrice(newPrice)
    }

    const handleSellingPlanUpdate = (newSellingPlanId) => {
      setSellingPlanId(newSellingPlanId)
    }

    globalEmitter.on(emitterProps.product.updatePrice, handlePriceUpdate)
    globalEmitter.on(emitterProps.product.updateSellingPlan, handleSellingPlanUpdate)

    return () => {
      globalEmitter.off(emitterProps.product.updatePrice, handlePriceUpdate)
      globalEmitter.off(emitterProps.product.updateSellingPlan, handleSellingPlanUpdate)
    }
  }, [])

  useEffect(() => {
    setCurrentVariantPrice(currentVariant.price)
    setSellingPlanId(null)
  }, [currentVariant])

  const productATCContent = enableBackInStock ? (
    <ButtonBIS
      product={initProduct}
      currentVariant={currentVariant}
      buttonText={!isMobile ? translate.product.notifyMeWhenAvailable : translate.product.notifyMe}
      classButton="button-primary relative w-fit min-w-[197px] uppercase md-max:min-w-[145px] sm-h-max:button-small"
    />
  ) : (
    <ProductATC
      title={title}
      productBundles={productBundles}
      buttonText={isMobile ? 'Add' : translate.general.addToCart}
      currentVariant={currentVariant}
      isEnabledInput={false}
      buttonClass="button button-primary w-fit !mt-0 min-w-[197px] md-max:min-w-[145px] sm-h-max:button-small"
      updateCurrentVariant={updateCurrentVariant}
      classWrap=""
      currentVariantPrice={currentVariantPrice}
      sellingPlanId={sellingPlanId}
      initProduct={initProduct}
    />
  )

  const productBundleATCContent = (
    <ProductBundleFormATC
      isStickyATC
      bundles={bundles}
      minItemsRequired={minItemsRequired}
      isBundleFixed={isBundleFixed}
      sellingPlanId={sellingPlanId}
      currentVariant={currentVariant}
      initProduct={initProduct}
      currentVariantPrice={currentPrice}
      isDisabledATC={isDisabledATC}
      currentSellingPlan={currentSellingPlan}
      isMobile={isMobile}
    />
  )

  return (
    <div
      ref={stickyAtcRef}
      className={clsx(
        'fixed bottom-0 left-0 right-0 z-30 bg-white transition-all duration-500',
        isActive ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible translate-y-full opacity-0'
      )}
    >
      <div className="container flex items-center justify-between gap-4 border-t border-neutral-900 py-5 sm-h-max:py-2">
        <div>
          <h2 className="text-sm font-extrabold uppercase text-neutral-900 xs-h-max:text-xs">{title}</h2>
          <div className="mt-1 flex items-start gap-2">
            {type && <span className="text-xs text-neutral-700">{type}</span>}
            <button
              type="button"
              className="animation-link min-w-fit pb-0 text-xs font-normal text-neutral-700"
              onClick={handleEditClick}
            >
              {translate.product.edit}
            </button>
          </div>
        </div>
        {isBundle ? productBundleATCContent : productATCContent}
      </div>
    </div>
  )
}

export default ProductStickyATC
